import React from 'react';
import { AnimatePresence } from 'framer-motion';
import { MealDeadlineTimeline } from '@/components/config_forms/shared/help-graphics/MealDeadlineTimeline';
import { BreakCountTimeline } from '@/components/config_forms/shared/help-graphics/BreakCountTimeline';
import { useMethodSwapAnimation } from '@/components/config_forms/shared/surcharge/useMethodSwapAnimation';
import { SurchargeMethodPanel } from '@/components/config_forms/shared/surcharge/SurchargeMethodPanel';
import { MeasureTokenText } from '@/components/ui/MeasureTokenText';

interface TriggerModeDetailsContentProps {
  activeMode: 'deadline' | 'count';
  // Deadline props
  deadlineHours: number;
  gracePeriodMinutes: number;
  multipleBreaks: boolean;
  onDeadlineChange?: (value: number | null) => void;
  onMultipleBreaksChange?: (value: boolean) => void;
  onGracePeriodChange?: (value: number | null) => void;
  // Count props
  requiredBreaks: number;
  scalingEnabled: boolean;
  thresholdHours: number | null;
  additionalBreakIntervalHours: number | null;
  thresholdFromBilling: number | null;
  onRequiredBreaksChange?: (value: number | null) => void;
  onScalingToggle?: (enabled: boolean) => void;
  onThresholdChange?: (value: number | null) => void;
  onAdditionalBreakIntervalChange?: (value: number | null) => void;
}

const DeadlineContent: React.FC<
  Pick<TriggerModeDetailsContentProps, 'deadlineHours' | 'gracePeriodMinutes' | 'multipleBreaks' | 'onDeadlineChange' | 'onMultipleBreaksChange' | 'onGracePeriodChange'>
> = ({ deadlineHours, gracePeriodMinutes, multipleBreaks, onDeadlineChange, onMultipleBreaksChange, onGracePeriodChange }) => (
  <div>
    <h3 className="text-base leading-none font-bold text-primary-text my-4">How It Works</h3>
    <p className="leading-relaxed text-popover-foreground/90 mb-4">
      <MeasureTokenText text="The clock starts when the shift begins. If no qualifying meal break is taken before the " labelPrefix="Meal Trigger" />
      <strong><MeasureTokenText text="Meal Break Deadline" labelPrefix="Meal Trigger" /></strong>
      <MeasureTokenText text=", the surcharge kicks in. A " labelPrefix="Meal Trigger" />
      <strong><MeasureTokenText text="Grace Period" labelPrefix="Meal Trigger" /></strong>
      <MeasureTokenText text=", if configured, absorbs minor overruns — the surcharge only fires once the grace period is also exceeded." labelPrefix="Meal Trigger" />
    </p>

    <MealDeadlineTimeline
      deadlineHours={deadlineHours}
      gracePeriodMinutes={gracePeriodMinutes}
      multipleBreaks={multipleBreaks}
      onDeadlineChange={onDeadlineChange}
      onMultipleBreaksChange={onMultipleBreaksChange}
      onGracePeriodChange={onGracePeriodChange}
    />
  </div>
);

const CountContent: React.FC<
  Pick<TriggerModeDetailsContentProps,
    'requiredBreaks' | 'scalingEnabled' | 'thresholdHours' |
    'additionalBreakIntervalHours' | 'thresholdFromBilling' |
    'onRequiredBreaksChange' | 'onScalingToggle' |
    'onThresholdChange' | 'onAdditionalBreakIntervalChange'>
> = (props) => (
  <div>
    <h3 className="text-base font-bold text-primary-text mb-3 mt-2">How It Works</h3>
    <p className="leading-relaxed text-popover-foreground/90 mb-4">
      <MeasureTokenText text="The system counts qualifying meal breaks at shift end. Required breaks scale with shift length when scaling is enabled — each completed interval beyond the threshold adds one more required break." labelPrefix="Meal Trigger" />
    </p>
    <BreakCountTimeline {...props} />
  </div>
);

export const TriggerModeDetailsContent: React.FC<TriggerModeDetailsContentProps> = ({
  activeMode,
  deadlineHours,
  gracePeriodMinutes,
  multipleBreaks,
  onDeadlineChange,
  onMultipleBreaksChange,
  onGracePeriodChange,
  requiredBreaks,
  scalingEnabled,
  thresholdHours,
  additionalBreakIntervalHours,
  thresholdFromBilling,
  onRequiredBreaksChange,
  onScalingToggle,
  onThresholdChange,
  onAdditionalBreakIntervalChange,
}) => {
  const {
    containerStyle,
    containerRef,
    activePanelRef,
    fadeDuration,
  } = useMethodSwapAnimation(activeMode);

  return (
    <div className="pt-1 border-t border-border/40">
      <div ref={containerRef} className="overflow-hidden" style={containerStyle}>
        <div className="relative grid">
          <AnimatePresence mode="sync" initial={false}>
            {activeMode === 'deadline' && (
              <SurchargeMethodPanel key="deadline" activePanelRef={activePanelRef} transitionDuration={fadeDuration}>
                <DeadlineContent
                  deadlineHours={deadlineHours}
                  gracePeriodMinutes={gracePeriodMinutes}
                  multipleBreaks={multipleBreaks}
                  onDeadlineChange={onDeadlineChange}
                  onMultipleBreaksChange={onMultipleBreaksChange}
                  onGracePeriodChange={onGracePeriodChange}
                />
              </SurchargeMethodPanel>
            )}
            {activeMode === 'count' && (
              <SurchargeMethodPanel key="count" activePanelRef={activePanelRef} transitionDuration={fadeDuration}>
                <CountContent
                  requiredBreaks={requiredBreaks}
                  scalingEnabled={scalingEnabled}
                  thresholdHours={thresholdHours}
                  additionalBreakIntervalHours={additionalBreakIntervalHours}
                  thresholdFromBilling={thresholdFromBilling}
                  onRequiredBreaksChange={onRequiredBreaksChange}
                  onScalingToggle={onScalingToggle}
                  onThresholdChange={onThresholdChange}
                  onAdditionalBreakIntervalChange={onAdditionalBreakIntervalChange}
                />
              </SurchargeMethodPanel>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
